import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PartnerDetailContent from '../components/PartnerDetailContent';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { X, Monitor, Smartphone, Eye, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

/**
 * PartnerPreviewSandbox — Aperçu visuel Retailer sécurisé (mode sandbox)
 * 
 * Utilise le composant unifié PartnerDetailContent en mode="preview"
 * 
 * ARCHITECTURE UNIFIÉE:
 * - Même rendu que PartnerPage (Retailer)
 * - Navigation désactivée via mode="preview"
 * - Accès restreint Admin/Partner uniquement
 * 
 * ROUTE: /dashboard/partner-preview/:id
 */
const PartnerPreviewSandbox = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('desktop');

  useEffect(() => {
    if (user && !['admin', 'partenaire'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadPartner = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/partners/${id}`);
        setPartner(response.data);
      } catch (err) {
        try {
          const response = await api.get(`/partners/slug/${id}`);
          setPartner(response.data);
        } catch {
          setError('Partenaire introuvable');
        }
      } finally {
        setLoading(false);
      }
    };
    if (id) loadPartner();
  }, [id]);

  const handleClose = () => {
    window.close();
    setTimeout(() => navigate(-1), 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement de la prévisualisation...</p>
        </div>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Erreur de prévisualisation</h2>
            <p className="text-muted-foreground mb-4">{error || 'Partenaire introuvable'}</p>
            <Button onClick={handleClose}>Fermer</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const displayName = partner.nom || partner.name || 'Partenaire';

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header sandbox — identique à OfferPreviewSandbox */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Eye className="h-5 w-5 text-purple-400" />
            <div>
              <h1 className="text-white font-semibold">Prévisualisation Fiche Partenaire</h1>
              <p className="text-gray-400 text-sm">Aperçu Retailer — {displayName}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Toggle Desktop/Mobile */}
            <div className="flex items-center gap-1 bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('desktop')}
                className={`p-2 rounded ${viewMode === 'desktop' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}
                title="Vue Desktop"
                data-testid="preview-desktop-toggle"
              >
                <Monitor className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('mobile')}
                className={`p-2 rounded ${viewMode === 'mobile' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}
                title="Vue Mobile"
                data-testid="preview-mobile-toggle"
              >
                <Smartphone className="h-4 w-4" />
              </button>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              className="text-white border-gray-600 hover:bg-gray-700"
              data-testid="preview-close-btn"
            >
              <X className="h-4 w-4 mr-2" />
              Fermer
            </Button>
          </div>
        </div>
      </div>

      {/* Zone de preview — SANDBOX MODE */}
      <div className="flex justify-center p-4 sm:p-8">
        <div
          className={`bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300 ${
            viewMode === 'mobile' ? 'w-[375px]' : 'w-full max-w-5xl'
          }`}
        >
          {/* Barre de simulation navigateur */}
          <div className="bg-gray-100 border-b px-4 py-2 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 bg-white rounded px-3 py-1 text-sm text-gray-500 truncate ml-2">
              gamesvisor.com/partner/{partner.slug || id}
            </div>
          </div>

          {/* Contenu — Composant unifié en mode preview */}
          <div className="overflow-auto max-h-[calc(100vh-200px)]">
            <PartnerDetailContent
              partner={partner}
              slug={partner.slug || id}
              mode="preview"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerPreviewSandbox;
